// ./src/components/features/ExpenseFiltersToolbar/ExpenseFiltersDrawer.tsx
//
// Filters drawer for expense-based pages (Logbook, Travels, Revenues, etc.).
// Uses AppDrawer (bottom sheet on mobile, side drawer on desktop).
//
// Draft state pattern:
// - On open, copies currently applied filters from EntityFiltersContext into
//   local draft state.
// - User edits draft freely inside the drawer.
// - "Apply" commits all draft values to context at once, then calls onApply
//   with the committed values so the toolbar can emit them synchronously.
// - "Clear" resets draft to defaults.
// - Close/Cancel discards draft silently (no isDirty confirmation).
//
// IMPORTANT: Apply only commits filters that are VISIBLE in the drawer
// (controlled by show* props). Hidden filters are not touched so the
// drawer doesn't accidentally overwrite context state managed by other
// parts of the app (e.g. carId from the mobile vehicle switcher).

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Stack, Text, Box, Divider, Group, Button } from "@mantine/core";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useEntityFiltersContext } from "@/contexts/EntityFiltersContext";
import { ExpenseTypeMode } from "@/config/expenseTypes";

import { AppDrawer } from "@/components/generic/AppDrawer";
import { DateRangeFilter, DateRange } from "@/components/generic/DateRangeFilter";
import { TagsSelectorInput } from "@/components/generic/TagsSelectorInput";
import { ExpenseTypeSelector } from "@/components/generic/ExpenseTypeSelector";

import { CarSelector } from "@/components/features/Cars/CarSelector";
import { MultiCarSelector } from "@/components/features/Cars/MultiCarSelector";
import { ExpenseKindMultiSelector } from "@/components/features/ExpenseKindSelector/ExpenseKindMultiSelector";

// ============================================
// Types
// ============================================

/** Controls which car filter variant is shown */
export type CarFilterMode = "none" | "single" | "multi";

/**
 * Built-in filter values committed by the drawer on Apply.
 * Only includes values for filters that are visible (show* = true).
 * The toolbar uses these to build the combined filter object
 * synchronously, without reading from (potentially stale) context.
 */
export interface DrawerApplyValues {
  carId?: string | null;
  multiCarIds?: string[] | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  tagIds?: string[];
  expenseType?: number | null;
  expenseKindIds?: number[];
}

/**
 * Interface for page-specific extra filters to participate in the
 * drawer's Apply / Clear / Open lifecycle.
 */
export interface ExtraFiltersHandle {
  /** Called when the drawer opens — sync applied values into draft state */
  onOpen?: () => void;

  /**
   * Called when the user clicks "Apply" — commit draft values to real state.
   * Should return the extra filter values being applied so the toolbar can
   * include them in onFiltersChange without waiting for async state updates.
   */
  onApply: () => Record<string, unknown> | void;

  /** Called when the user clicks "Clear" — reset draft values to defaults */
  onClear: () => void;

  /** Return true if the extra filters have any active draft values */
  hasDraftValues?: () => boolean;

  /** Return current applied (not draft) extra filter values for inclusion in onFiltersChange */
  getValues?: () => Record<string, unknown>;
}

export interface ExpenseFiltersDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Close the drawer (Cancel / dismiss — discards draft) */
  onClose: () => void;

  /**
   * Called after Apply commits all values. Receives the built-in values
   * that were just committed so the toolbar can build the combined filter
   * synchronously without reading from (stale) context.
   * If not provided, the drawer calls onClose after Apply (legacy behavior).
   */
  onApply?: (values: DrawerApplyValues) => void;

  /**
   * Car filter mode:
   * - "none"   — no car filter shown
   * - "single" — single car selector (default)
   * - "multi"  — multi-car selector
   */
  carFilterMode?: CarFilterMode;

  /** Show date range filter (default: true) */
  showDateFilter?: boolean;

  /** Show tags filter (default: true) */
  showTagFilter?: boolean;

  /** Show expense type filter (default: false) */
  showExpenseTypeFilter?: boolean;

  /** Which expense types to show */
  expenseTypeMode?: ExpenseTypeMode;

  /** Show expense kind multi-select filter (default: false) */
  showExpenseKindFilter?: boolean;

  /** Currently applied expense kind IDs (managed by toolbar) */
  expenseKindIds?: number[];

  /** Callback when expense kind selection changes */
  onExpenseKindChange?: (kindIds: number[]) => void;

  /** User has to select a car (single mode only) */
  carRequired?: boolean;

  /** Additional filter components rendered at the end */
  extraFilters?: React.ReactNode;

  /**
   * Ref to the extra filters handle so the drawer can call onApply/onClear/onOpen
   * on page-specific filters.
   */
  extraFiltersRef?: React.RefObject<ExtraFiltersHandle | null>;

  /** Current multi-car IDs (managed by toolbar for multi mode) */
  multiCarIds?: string[] | null;

  /** Callback when multi-car selection changes */
  onMultiCarChange?: (carIds: string[] | null) => void;
}

// ============================================
// Component
// ============================================

export const ExpenseFiltersDrawer: React.FC<ExpenseFiltersDrawerProps> = ({
  opened,
  onClose,
  onApply: onApplyProp,
  carFilterMode = "single",
  showDateFilter = true,
  showTagFilter = true,
  showExpenseTypeFilter = false,
  expenseTypeMode = "all",
  showExpenseKindFilter = false,
  expenseKindIds,
  onExpenseKindChange,
  carRequired = false,
  extraFilters,
  extraFiltersRef,
  multiCarIds,
  onMultiCarChange,
}) => {
  const { t } = useTranslation(["common", "expenses"]);

  const {
    filters,
    setCarId,
    setDateRange,
    setTagIds,
    setExpenseType,
  } = useEntityFiltersContext();

  // ============================================
  // Draft State
  // ============================================

  const [draftCarId, setDraftCarId] = useState<string | null>(null);
  const [draftMultiCarIds, setDraftMultiCarIds] = useState<string[] | null>(null);
  const [draftDateFrom, setDraftDateFrom] = useState<string | null>(null);
  const [draftDateTo, setDraftDateTo] = useState<string | null>(null);
  const [draftTagIds, setDraftTagIds] = useState<string[]>([]);
  const [draftExpenseType, setDraftExpenseType] = useState<number | null>(null);
  const [draftExpenseKindIds, setDraftExpenseKindIds] = useState<number[]>([]);

  // Copy applied filters into draft when drawer opens
  useEffect(() => {
    if (opened) {
      setDraftCarId(filters.carId ?? null);
      setDraftMultiCarIds(multiCarIds ?? null);
      setDraftDateFrom(filters.dateFrom ?? null);
      setDraftDateTo(filters.dateTo ?? null);
      setDraftTagIds(filters.tagIds ?? []);
      setDraftExpenseType(filters.expenseType ?? null);
      setDraftExpenseKindIds(expenseKindIds ?? []);

      extraFiltersRef?.current?.onOpen?.();
    }
  }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // Draft has any active filters?
  // ============================================

  const draftHasFilters = useMemo(() => {
    if (carFilterMode === "single" && draftCarId) return true;
    if (carFilterMode === "multi" && draftMultiCarIds && draftMultiCarIds.length > 0) return true;
    if (showDateFilter && (draftDateFrom || draftDateTo)) return true;
    if (showTagFilter && draftTagIds.length > 0) return true;
    if (showExpenseTypeFilter && draftExpenseType) return true;
    if (showExpenseKindFilter && draftExpenseKindIds.length > 0) return true;
    if (extraFiltersRef?.current?.hasDraftValues?.()) return true;
    return false;
  }, [
    carFilterMode, draftCarId, draftMultiCarIds,
    showDateFilter, draftDateFrom, draftDateTo,
    showTagFilter, draftTagIds,
    showExpenseTypeFilter, draftExpenseType,
    showExpenseKindFilter, draftExpenseKindIds,
    extraFiltersRef,
  ]);

  // ============================================
  // Date Range value for DateRangeFilter
  // ============================================

  const draftDateRange = useMemo<DateRange>(
    () => ({
      from: draftDateFrom ? dayjs(draftDateFrom).toDate() : null,
      to: draftDateTo ? dayjs(draftDateTo).toDate() : null,
    }),
    [draftDateFrom, draftDateTo]
  );

  // ============================================
  // Handlers
  // ============================================

  const handleCarChange = useCallback(
    (_name: string, value: string | null) => {
      setDraftCarId(value);
    },
    []
  );

  const handleMultiCarChange = useCallback(
    (_name: string, value: string[] | null) => {
      setDraftMultiCarIds(value);
    },
    []
  );

  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDraftDateFrom(
      range.from ? dayjs(range.from).format("YYYY-MM-DD") : null
    );
    setDraftDateTo(range.to ? dayjs(range.to).format("YYYY-MM-DD") : null);
  }, []);

  const handleTagsChange = useCallback(
    (_name: string, tagIds: string[]) => {
      setDraftTagIds(tagIds);
    },
    []
  );

  const handleExpenseTypeChange = useCallback(
    (_name: string, value: number | null) => {
      setDraftExpenseType(value);
    },
    []
  );

  const handleExpenseKindChange = useCallback(
    (_name: string, value: number[]) => {
      setDraftExpenseKindIds(value);
    },
    []
  );

  // ============================================
  // Apply / Clear
  // ============================================

  const handleApply = useCallback(() => {
    // Only commit filters that are VISIBLE in this drawer.
    if (carFilterMode === "single") {
      setCarId(draftCarId);
    } else if (carFilterMode === "multi") {
      onMultiCarChange?.(draftMultiCarIds);
    }

    if (showDateFilter) {
      setDateRange(draftDateFrom, draftDateTo);
    }

    if (showTagFilter) {
      setTagIds(draftTagIds);
    }

    if (showExpenseTypeFilter) {
      setExpenseType(draftExpenseType);
    }

    if (showExpenseKindFilter) {
      onExpenseKindChange?.(draftExpenseKindIds);
    }

    // Commit extra filters drafts
    extraFiltersRef?.current?.onApply();

    if (onApplyProp) {
      const values: DrawerApplyValues = {};

      if (carFilterMode === "single") {
        values.carId = draftCarId;
      }
      if (carFilterMode === "multi") {
        values.multiCarIds = draftMultiCarIds;
      }
      if (showDateFilter) {
        values.dateFrom = draftDateFrom;
        values.dateTo = draftDateTo;
      }
      if (showTagFilter) {
        values.tagIds = draftTagIds;
      }
      if (showExpenseTypeFilter) {
        values.expenseType = draftExpenseType;
      }
      if (showExpenseKindFilter) {
        values.expenseKindIds = draftExpenseKindIds;
      }

      onApplyProp(values);
    } else {
      onClose();
    }
  }, [
    carFilterMode,
    draftCarId,
    draftMultiCarIds,
    draftDateFrom,
    draftDateTo,
    draftTagIds,
    draftExpenseType,
    draftExpenseKindIds,
    showDateFilter,
    showTagFilter,
    showExpenseTypeFilter,
    showExpenseKindFilter,
    setCarId,
    setDateRange,
    setTagIds,
    setExpenseType,
    onMultiCarChange,
    onExpenseKindChange,
    extraFiltersRef,
    onApplyProp,
    onClose,
  ]);

  const handleClear = useCallback(() => {
    setDraftCarId(null);
    setDraftMultiCarIds(null);
    setDraftDateFrom(null);
    setDraftDateTo(null);
    setDraftTagIds([]);
    setDraftExpenseType(null);
    setDraftExpenseKindIds([]);

    extraFiltersRef?.current?.onClear();
  }, [extraFiltersRef]);

  // ============================================
  // Custom Action Buttons
  // ============================================

  const customActions = useMemo(
    () => (
      <Group gap="sm" grow>
        {draftHasFilters && (
          <Button variant="default" size="lg" onClick={handleClear}>
            {t("common:buttons.clear")}
          </Button>
        )}
        <Button size="lg" onClick={handleApply}>
          {t("common:buttons.apply")}
        </Button>
      </Group>
    ),
    [draftHasFilters, handleClear, handleApply, t]
  );

  // ============================================
  // Render
  // ============================================

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t("common:buttons.filters", { defaultValue: "Filters" })}
      position="right"
      size="md"
      responsive
      mobileHeightPercent={80}
      actionType="custom"
      customActions={customActions}
      isDirty={false}
    >
      <Stack gap="lg">
        {carFilterMode === "single" && (
          <Box>
            <Text size="sm" fw={500} mb={6} c="dimmed">
              {t("common:navigation.cars")}
            </Text>
            <CarSelector
              name="carFilter"
              value={draftCarId}
              onChange={handleCarChange}
              showAllOption={!carRequired}
              placeholder={t("common:filters.allVehicles")}
              required={carRequired}
              comboboxProps={{ withinPortal: false }}
            />
          </Box>
        )}

        {carFilterMode === "multi" && (
          <Box>
            <Text size="sm" fw={500} mb={6} c="dimmed">
              {t("common:navigation.cars")}
            </Text>
            <MultiCarSelector
              value={draftMultiCarIds}
              onChange={handleMultiCarChange}
              showAllOption
              size="md"
              placeholder={t("common:filters.allVehicles")}
            />
          </Box>
        )}

        {showExpenseTypeFilter && (
          <>
            <Divider />
            <Box>
              <ExpenseTypeSelector
                name="expenseTypeFilter"
                value={draftExpenseType}
                onChange={handleExpenseTypeChange}
                mode={expenseTypeMode}
                label={t("common:filters.expenseType", {
                  defaultValue: "Type",
                })}
                size="md"
              />
            </Box>
          </>
        )}

        {showExpenseKindFilter && (
          <>
            <Divider />
            <Box>
              <ExpenseKindMultiSelector
                name="expenseKindFilter"
                value={draftExpenseKindIds}
                onChange={handleExpenseKindChange}
                label={t("expenses:form.fields.expenseKind.label")}
                quickAccessCodes={[]}
                size="md"
              />
            </Box>
          </>
        )}

        {showTagFilter && (
          <>
            <Divider />
            <Box>
              <Text size="sm" fw={500} mb={6} c="dimmed">
                {t("common:pages.tags.name")}
              </Text>
              <TagsSelectorInput
                name="tagFilter"
                value={draftTagIds}
                onChange={handleTagsChange}
                placeholder={t("common:filters.filterByTags")}
                size="md"
                maxTags={5}
                withinPortal={false}
              />
            </Box>
          </>
        )}

        {showDateFilter && (
          <>
            <Divider />
            <Box>
              <Text size="sm" fw={500} mb={6} c="dimmed">
                {t("common:filters.dateRange")}
              </Text>
              <DateRangeFilter
                value={draftDateRange}
                onChange={handleDateRangeChange}
                layout="stacked"
              />
            </Box>
          </>
        )}

        {extraFilters && (
          <>
            <Divider />
            <Box>{extraFilters}</Box>
          </>
        )}
      </Stack>
    </AppDrawer>
  );
};